import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Edit, Plus, Trash2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function RacesTab({ races, onRefresh, onDelete }) {
  const [isOpen, setIsOpen] = useState(false);
  const [editingRace, setEditingRace] = useState(null);
  const [formData, setFormData] = useState({ name: '', location: '', date: '', status: 'upcoming' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingRace) {
        await axios.put(`${BACKEND_URL}/api/races/${editingRace.race_id}`, formData, { withCredentials: true });
        toast.success('Race updated');
      } else {
        await axios.post(`${BACKEND_URL}/api/races`, formData, { withCredentials: true });
        toast.success('Race created');
      }
      setIsOpen(false);
      setEditingRace(null);
      setFormData({ name: '', location: '', date: '', status: 'upcoming' });
      onRefresh();
    } catch (error) {
      toast.error('Failed to save race');
    }
  };

  const openEdit = (race) => {
    setEditingRace(race);
    setFormData({ name: race.name, location: race.location, date: race.date, status: race.status });
    setIsOpen(true);
  };

  return (
    <div className="bg-card border border-zinc-800 p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="font-heading font-bold text-2xl">Races Management</h2>
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
          <DialogTrigger asChild>
            <button
              data-testid="add-race-button"
              onClick={() => { setEditingRace(null); setFormData({ name: '', location: '', date: '', status: 'upcoming' }); }}
              className="btn-skew bg-primary text-white px-6 py-2 font-bold uppercase flex items-center space-x-2"
            >
              <Plus size={18} />
              <span>Add Race</span>
            </button>
          </DialogTrigger>
          <DialogContent className="bg-card border border-zinc-800">
            <DialogHeader>
              <DialogTitle className="font-heading font-bold text-2xl">
                {editingRace ? 'Edit Race' : 'Add Race'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-2">Name</label>
                <input type="text" data-testid="race-name-input" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="w-full bg-zinc-950 border border-zinc-800 focus:border-primary focus:ring-1 focus:ring-primary h-12 px-4 text-white" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-2">Location</label>
                <input type="text" data-testid="race-location-input" value={formData.location} onChange={(e) => setFormData({ ...formData, location: e.target.value })} className="w-full bg-zinc-950 border border-zinc-800 focus:border-primary focus:ring-1 focus:ring-primary h-12 px-4 text-white" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-2">Date</label>
                <input type="date" data-testid="race-date-input" value={formData.date} onChange={(e) => setFormData({ ...formData, date: e.target.value })} className="w-full bg-zinc-950 border border-zinc-800 focus:border-primary focus:ring-1 focus:ring-primary h-12 px-4 text-white" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-2">Status</label>
                <select data-testid="race-status-select" value={formData.status} onChange={(e) => setFormData({ ...formData, status: e.target.value })} className="w-full bg-zinc-950 border border-zinc-800 focus:border-primary focus:ring-1 focus:ring-primary h-12 px-4 text-white">
                  <option value="upcoming">Upcoming</option>
                  <option value="completed">Completed</option>
                </select>
              </div>
              <button type="submit" data-testid="save-race-button" className="w-full bg-primary text-white py-3 font-bold uppercase">
                {editingRace ? 'Update' : 'Create'}
              </button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-zinc-800">
              <th className="text-left p-4 font-heading font-bold text-sm text-zinc-400">NAME</th>
              <th className="text-left p-4 font-heading font-bold text-sm text-zinc-400">LOCATION</th>
              <th className="text-left p-4 font-heading font-bold text-sm text-zinc-400">DATE</th>
              <th className="text-left p-4 font-heading font-bold text-sm text-zinc-400">STATUS</th>
              <th className="text-right p-4 font-heading font-bold text-sm text-zinc-400">ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            {races.map((race) => (
              <tr key={race.race_id} className="border-b border-zinc-800 hover:bg-zinc-900">
                <td className="p-4 font-bold">{race.name}</td>
                <td className="p-4 text-zinc-400">{race.location}</td>
                <td className="p-4 text-zinc-400">{race.date}</td>
                <td className="p-4">
                  <span className={`px-2 py-1 text-xs font-bold uppercase ${
                    race.status === 'completed' ? 'bg-green-900 text-green-300' : 'bg-yellow-900 text-yellow-300'
                  }`}>
                    {race.status}
                  </span>
                </td>
                <td className="p-4 text-right">
                  <button onClick={() => openEdit(race)} className="text-secondary hover:text-white mr-3">
                    <Edit size={18} />
                  </button>
                  <button onClick={() => onDelete(race.race_id)} className="text-red-500 hover:text-red-400">
                    <Trash2 size={18} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

